import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useId,
  useState,
  useEffect,
} from 'react';
import {
  Flex,
  Text,
  Button,
  chakra,
  Center,
  Progress,
  Box,
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { Base } from '../Base';

const ERROR_STYLE = {
  dropZone: {
    outline: '2px solid',
    outlineOffset: '1.5px',
    outlineColor: 'red.500',
  },
};

export const FileField = forwardRef(({
  name,
  value,
  error,
  accept,
  onBlur,
  onChange,
  isDisabled,
  progress = 0,
  iconElement = null,
  placeholder = 'Upload a file',
  'data-testid': dataTestId,
  ...props
}, ref) => {
  const inputRef = useRef(null);
  const uid = useId();
  const elementId = `fileField-${uid}`;
  const showProgress = progress > 0;
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const errorStyle = error ? ERROR_STYLE : { dropZone: {} };

  const onUploadClick = (e) => {
    e.stopPropagation();
    inputRef.current.click();
  };

  const onZoneClick = () => {
    inputRef.current.click();
  };

  useImperativeHandle(ref, () => ({
    onClick: () => inputRef.current.click(),
  }));

  const testIds = dataTestId ? {
    base: dataTestId,
    input: `${dataTestId}.input`,
  } : {};

  useEffect(() => {
    if (progress === 100) {
      setIsIndeterminate(true);
    }
  }, [progress]);

  return (
    <Base
      name={name}
      data-testid={testIds.base}
      error={error}
      {...props}
      id={elementId}
    >
      <Flex
        gap="2"
        padding="3"
        width="full"
        cursor="pointer"
        borderRadius="lg"
        borderWidth="1px"
        alignItems="center"
        borderStyle="dashed"
        onClick={onZoneClick}
        borderColor={isDisabled ? 'gray.400' : 'blue.500'}
        bgColor={isDisabled ? 'gray.100' : 'white'}
        justifyContent="flex-start"
        {...errorStyle.dropZone}
      >
        <chakra.input
          id={elementId}
          type="file"
          name={name}
          value={value}
          ref={inputRef}
          onBlur={onBlur}
          visibility="hidden"
          position="absolute"
          onChange={onChange}
          accept={accept}
          disabled={isDisabled}
          data-testid={testIds.input}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        />
        {iconElement && (
          <Center
            boxSize="7"
            borderRadius="lg"
            bgColor={isDisabled ? 'gray.200' : 'gray.100'}
          >
            {iconElement}
          </Center>
        )}
        {showProgress && (
          <Box width="full">
            <Text fontSize="sm">
              Cargando archivo (
              {progress}
              )%
            </Text>
            <Progress
              size="sm"
              colorScheme="blue"
              variant="subtle"
              borderRadius="lg"
              width="full"
              value={progress}
              isIndeterminate={isIndeterminate}
            />
          </Box>
        )}
        {!showProgress && (
          <Text
            fontSize="md"
            color={isDisabled ? 'gray.400' : 'gray.700'}
            dir="rtl"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            maxWidth="56"
          >
            {value || placeholder}
          </Text>
        )}
        <Button
          onClick={onUploadClick}
          variant="ghost"
          size="sm"
          marginLeft="auto"
          isDisabled={isDisabled}
          rightIcon={<ArrowUpIcon />}
        >
          Subir
        </Button>
      </Flex>
    </Base>
  );
});
